import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  PlugZap,
  RefreshCw,
  Save,
  Unlink2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

const ORIGIN =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

export default function XeroSettings() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/xero/status`, { params: { origin: ORIGIN } });
      setStatus(r.data);
    } catch (e) {
      toast.error("Failed to load Xero status", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Surface OAuth round-trip outcome and clear the query string.
  useEffect(() => {
    if (params.get("xero") === "connected") {
      toast.success("Xero connected");
      setParams((p) => {
        const np = new URLSearchParams(p);
        np.delete("xero");
        return np;
      });
      refresh();
    }
    const err = params.get("xero_error");
    if (err) {
      toast.error("Xero connection failed", { description: err });
      setParams((p) => {
        const np = new URLSearchParams(p);
        np.delete("xero_error");
        return np;
      });
    }
  }, [params, setParams, refresh]);

  // Deep-link auto-reconnect (sync-status banner "Fix" button).
  // When the URL contains ``?reconnect=xero`` we wait for the status
  // fetch to complete and, if credentials are configured but the
  // connection is broken, kick off the OAuth flow automatically. The
  // query param is stripped before we redirect so a back-navigation
  // after the OAuth round-trip doesn't retrigger. Guarded by a ref
  // so React 18 strict-mode double-invoke can't fire twice.
  const autoReconnectFired = useRef(false);
  useEffect(() => {
    if (autoReconnectFired.current) return;
    if (params.get("reconnect") !== "xero") return;
    if (!status) return;  // wait for /xero/status
    // Strip the param first so any error path (or a second render)
    // doesn't loop.
    setParams((p) => {
      const np = new URLSearchParams(p);
      np.delete("reconnect");
      return np;
    }, { replace: true });
    if (!status.configured) {
      toast.info("Xero credentials missing", {
        description: "Save your Client ID + Secret first, then click Connect to Xero.",
      });
      return;
    }
    if (status.connected) {
      toast.success("Xero is already connected");
      return;
    }
    autoReconnectFired.current = true;
    toast.info("Reconnecting to Xero…");
    startOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, status]);

  async function saveCredentials() {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.error("Both Client ID and Client Secret are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/xero/credentials", {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      toast.success("Xero credentials saved");
      setClientSecret("");
      await refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function startOAuth() {
    setConnecting(true);
    try {
      const r = await api.get("/xero/authorize", { params: { origin: ORIGIN } });
      // Hard navigate to Xero so the user can sign in & authorize.
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Could not start Xero authorization", {
        description: e?.response?.data?.detail || e.message,
      });
      setConnecting(false);
    }
  }

  async function disconnect(forgetCreds) {
    setDisconnecting(true);
    try {
      await api.delete(`/xero/disconnect`, {
        params: { keep_credentials: !forgetCreds },
      });
      toast.success(forgetCreds ? "Xero disconnected and credentials cleared" : "Xero disconnected");
      await refresh();
    } catch (e) {
      toast.error("Disconnect failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDisconnecting(false);
    }
  }

  async function copyRedirect() {
    const uri = status?.redirect_uri;
    if (!uri) return;
    try {
      await navigator.clipboard.writeText(uri);
      toast.success("Redirect URI copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }

  const connected = !!status?.connected;
  const configured = !!status?.configured;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="xero-settings">
      <Card className="lg:col-span-2 p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none bg-zinc-50">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <PlugZap className="h-3.5 w-3.5" /> Xero Payroll AU
        </div>

        <div className="mt-4 space-y-5">
          <div className="rounded-md border border-zinc-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">
              Redirect URI
            </div>
            <div className="flex items-center justify-between gap-3">
              <code
                className="text-xs font-mono-num text-zinc-800 break-all"
                data-testid="xero-redirect-uri"
              >
                {status?.redirect_uri || "—"}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={copyRedirect}
                data-testid="xero-copy-redirect"
                className="border-zinc-300 hover:bg-zinc-100 h-8 shrink-0"
              >
                Copy
              </Button>
            </div>
            <p className="mt-2 text-2xs text-zinc-500 leading-snug">
              Add this URL to your Xero app under{" "}
              <strong>OAuth 2.0 redirect URIs</strong> before connecting.
            </p>
          </div>

          <div>
            <Label className="text-zinc-700">Client ID</Label>
            <Input
              data-testid="xero-client-id"
              placeholder={configured ? "•••••••••• (saved)" : "Paste Xero Client ID"}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="mt-1.5 font-mono-num bg-white border-zinc-300 focus-visible:ring-zinc-950"
            />
          </div>

          <div>
            <Label className="text-zinc-700">Client Secret</Label>
            <div className="mt-1.5 relative">
              <Input
                data-testid="xero-client-secret"
                type={showSecret ? "text" : "password"}
                placeholder={configured ? "•••••••••• (saved)" : "Paste Xero Client Secret"}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="font-mono-num bg-white border-zinc-300 pr-10 focus-visible:ring-zinc-950"
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                data-testid="xero-toggle-secret-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                aria-label="Toggle secret visibility"
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">
              Stored encrypted in MongoDB. Leave blank to keep the existing secret.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={saveCredentials}
              disabled={saving}
              data-testid="xero-save-creds-btn"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? "Saving…" : "Save credentials"}
            </Button>
            <Button
              onClick={startOAuth}
              disabled={connecting || !configured}
              variant="outline"
              data-testid="xero-connect-btn"
              className="border-zinc-300 hover:bg-zinc-100"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              {connecting
                ? "Redirecting…"
                : connected
                ? "Re-authorize Xero"
                : "Connect to Xero"}
            </Button>
            {connected && (
              <Button
                onClick={() => disconnect(false)}
                disabled={disconnecting}
                variant="outline"
                data-testid="xero-disconnect-btn"
                className="border-zinc-300 hover:bg-zinc-100"
              >
                <Unlink2 className="h-4 w-4 mr-1.5" />
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Status
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            data-testid="xero-refresh-status"
            className="h-7 -mr-1.5 px-2 text-zinc-500 hover:text-zinc-900"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <Row
            label="Connection"
            value={connected ? "Connected" : configured ? "Credentials saved" : "Not configured"}
            ok={connected ? true : configured ? undefined : false}
          />
          <Row label="Organisation" value={status?.tenant_name || "—"} />
          <Row
            label="Connected at"
            value={status?.connected_at ? fmtDateTime(status.connected_at) : "—"}
          />
          <Row
            label="Last sync"
            value={status?.last_sync_at ? fmtDateTime(status.last_sync_at) : "Never"}
          />
          <Row
            label="Last sync count"
            value={status?.last_sync_count != null ? String(status.last_sync_count) : "—"}
          />
          {status?.last_sync_error && (
            <div className="text-2xs text-red-700 bg-red-50 border border-red-200 rounded p-2 break-words">
              {status.last_sync_error}
            </div>
          )}
          {connected && (
            <div className="flex items-center gap-1.5 text-emerald-700 text-xs pt-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Daily auto-sync at 1:00 AM Melbourne
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0 gap-3">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span
        className={
          "font-mono-num text-right break-all " +
          (ok === undefined ? "text-zinc-950" : ok ? "text-emerald-700" : "text-amber-700")
        }
      >
        {value}
      </span>
    </div>
  );
}
